/**
 * Admin Portal → Partners → a partner → Links & tags → a tag → Webhook (partner webhook intake,
 * workstream S5). Contract: S1's s1-webhook-intake.md §4 and the lead's answers of 2026-10-21.
 *
 * A partner's registration platform (Race Roster, Zapier, …) posts each person who ticked the
 * Coach Cedar box straight to this tag. The card:
 *   - creates the webhook URL, or rotates it — the full URL is shown ONCE (copy it now), and after
 *     a rotation the old URL keeps working until the time the server gives, then stops;
 *   - holds the consent facts the platform's box carries (platform name, the exact "Yes" wording,
 *     its version) and an optional opt-in check (a path whose value must match);
 *   - maps the platform's fields to ours (`{our field: their path}`), fed by the keys seen in the
 *     last delivery — keys only, never a value; a collapsed `answers.[].value` is offered as
 *     `answers.0.value`; the three event fields may instead be one fixed value for everyone;
 *   - switches it on (only once the consent facts and the email + first-name paths are saved) or off;
 *   - lists the recent deliveries: outcome, counts, what each key was used for (emergency-contact and
 *     medical keys are never stored — no switch, owner ruling), problems by record number.
 *
 * Every intake route is admins only (it carries personal data and a secret): a support or viewer
 * session sees a note and requests nothing. The card hides itself for an ended tag or partner.
 *
 * Web-only (the admin SPA).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AdminButton, AdminCard } from '../../../components/admin/ui';
import { PartnersAdmin } from '../../../services/partnersAdminService';
import { tagEditable, type PartnerStatus, type TagRow } from './partnersLogic';
import {
  CONSTANT_FIELDS,
  INTAKE_CONSENT_LIMITS,
  INTAKE_FIELDS,
  INTAKE_FIELD_LABELS,
  concretePath,
  deliveryCountsText,
  enableBlockers,
  fieldMapFormFrom,
  intakeConsentFormFrom,
  intakePatchBody,
  keyUseLabel,
  outcomeLabel,
  problemLines,
  seenPaths,
  type FieldMapEntry,
  type FieldMapForm,
  type IntakeConsentForm,
  type IntakeDelivery,
  type IntakeField,
  type IntakeRow,
} from './partnerIntakeLogic';
import {
  NoticeLine,
  StatusBadge,
  TypedConfirm,
  copyText,
  errText,
  fmtDateTime,
  hint,
  input,
  label,
  row,
  td,
  th,
  useBusy,
  type Notice,
} from './PartnersShared';

export const PartnerWebhookCard: React.FC<{
  tag: TagRow;
  partnerName: string;
  partnerStatus: PartnerStatus;
  writable: boolean;
}> = ({ tag, partnerName, partnerStatus, writable }) => {
  const live = tagEditable(tag.status, partnerStatus);
  const [intake, setIntake] = useState<IntakeRow | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [deliveries, setDeliveries] = useState<IntakeDelivery[]>([]);
  const [revealed, setRevealed] = useState<{ url: string; rotated: boolean } | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [consent, setConsent] = useState<IntakeConsentForm>(intakeConsentFormFrom(null));
  const [map, setMap] = useState<FieldMapForm>(fieldMapFormFrom(null));
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, run] = useBusy();

  const load = useCallback(async () => {
    try {
      const res = await PartnersAdmin.getIntake(tag.id);
      setIntake(res.intake ?? null);
      if (res.intake) {
        const d = await PartnersAdmin.intakeDeliveries(tag.id);
        setDeliveries(d.deliveries || []);
      } else {
        setDeliveries([]);
      }
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e) });
    } finally {
      setLoaded(true);
    }
  }, [tag.id]);

  useEffect(() => {
    if (!live || !writable) return;
    load();
  }, [load, live, writable]);

  // The forms follow the saved webhook (after a load, a save, a toggle).
  useEffect(() => {
    setConsent(intakeConsentFormFrom(intake));
    setMap(fieldMapFormFrom(intake?.field_map));
  }, [intake]);

  const seen = useMemo(() => seenPaths(deliveries), [deliveries]);
  const blockers = intake ? enableBlockers(intake) : [];
  const diff = intake ? intakePatchBody(intake, consent, map) : { body: {}, errors: [] as string[] };
  const dirty = Object.keys(diff.body).length > 0;

  // An ended tag or partner takes no deliveries (after the hooks — rules of hooks).
  if (!live) return null;

  if (!writable) {
    return (
      <AdminCard>
        <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Webhook</h4>
        <div style={hint} role="status">Webhook settings are admins only: they carry a secret URL and people&apos;s details.</div>
      </AdminCard>
    );
  }

  const issue = (rotate: boolean) =>
    run(async () => {
      try {
        const res = await PartnersAdmin.issueIntakeUrl(tag.id);
        setCopied(false);
        setRevealed({ url: res.intake_url, rotated: rotate });
        setIntake(res.intake);
        setConfirmRotate(false);
        setNotice(null);
        const d = await PartnersAdmin.intakeDeliveries(tag.id).catch(() => ({ deliveries: [] as IntakeDelivery[] }));
        setDeliveries(d.deliveries || []);
      } catch (e) {
        setConfirmRotate(false);
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const save = () =>
    run(async () => {
      if (!intake) return;
      if (diff.errors.length) { setNotice({ kind: 'error', text: 'Not saved:', extra: diff.errors }); return; }
      if (!dirty) { setNotice({ kind: 'info', text: 'Nothing changed.' }); return; }
      try {
        const res = await PartnersAdmin.updateIntake(tag.id, diff.body);
        setIntake(res.intake);
        setNotice({ kind: 'ok', text: 'Saved.' });
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const setEnabled = (enabled: boolean) =>
    run(async () => {
      try {
        const res = await PartnersAdmin.updateIntake(tag.id, { enabled });
        setIntake(res.intake);
        setNotice({ kind: 'ok', text: enabled ? 'On: the platform can send people to this tag now.' : 'Off: deliveries to this URL are refused.' });
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const setField = (f: IntakeField, patch: Partial<FieldMapEntry>) => setMap((m) => ({ ...m, [f]: { ...m[f], ...patch } }));
  const setC = (k: keyof IntakeConsentForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setConsent((c) => ({ ...c, [k]: e.target.value }));
  const latest = deliveries[0] ?? null;

  return (
    <AdminCard>
      <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Webhook</h4>
      <div style={hint}>
        {partnerName}&apos;s registration platform can send us each person who ticks the Coach Cedar box, as they register.
        The URL is a secret: anyone who has it can add people to this tag. People it adds wait on the Contacts tab for a
        person to press Send unless this tag sends automatically.
      </div>
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />

      {revealed && (
        <div role="alert" aria-label="Webhook URL" style={{ margin: '12px 0', padding: 12, borderRadius: 10, border: '1px solid var(--warning, #d97706)', background: 'var(--surface-subtle, #fffbeb)' }}>
          <b>{revealed.rotated ? 'The new webhook URL' : 'The webhook URL'} — copy it now. It will not be shown again.</b>
          <div style={{ fontSize: 12, margin: '6px 0' }}>
            Coach Cedar keeps only a one-way hash of it. Paste it into the platform&apos;s webhook settings; send it to the
            partner only through a private channel.
            {revealed.rotated && intake?.previous_url_valid_until
              ? ` The old URL keeps working until ${fmtDateTime(intake.previous_url_valid_until)}, then stops — update the platform before then.`
              : ''}
          </div>
          <div style={{ ...row, alignItems: 'center' }}>
            <code data-testid="webhook-url" style={{ wordBreak: 'break-all', fontSize: 13 }}>{revealed.url}</code>
            <AdminButton size="sm" variant="secondary" onClick={async () => setCopied(await copyText(revealed.url))}>{copied ? 'Copied' : 'Copy'}</AdminButton>
            <AdminButton size="sm" onClick={() => setRevealed(null)}>I&apos;ve saved it</AdminButton>
          </div>
        </div>
      )}

      {loaded && !intake && (
        <div style={{ marginTop: 8 }}>
          <div style={hint}>No webhook yet. Create its URL, then fill in the consent facts and the field map before turning it on.</div>
          <div style={{ marginTop: 8 }}>
            <AdminButton size="sm" onClick={() => issue(false)} disabled={busy}>Create webhook URL</AdminButton>
          </div>
        </div>
      )}

      {intake && (
        <>
          <div style={{ ...row, marginTop: 8, alignItems: 'center' }}>
            <StatusBadge status={intake.enabled ? 'on' : 'off'} tone={intake.enabled ? 'success' : 'neutral'} />
            <span style={hint}>
              {`URL issued ${fmtDateTime(intake.url_issued_at)}`}
              {intake.rotated_at ? ` · rotated ${fmtDateTime(intake.rotated_at)}` : ''}
              {` · last delivery ${intake.last_delivery_at ? fmtDateTime(intake.last_delivery_at) : 'none yet'}`}
            </span>
            <AdminButton size="sm" variant="secondary" onClick={() => setConfirmRotate(true)} disabled={busy}>Rotate URL</AdminButton>
            {intake.enabled
              ? <AdminButton size="sm" variant="secondary" onClick={() => setEnabled(false)} disabled={busy}>Turn off</AdminButton>
              : <AdminButton size="sm" onClick={() => setEnabled(true)} disabled={busy || blockers.length > 0 || dirty}>Turn on</AdminButton>}
          </div>
          {intake.previous_url_valid_until && (
            <div style={hint}>{`The old URL keeps working until ${fmtDateTime(intake.previous_url_valid_until)}, then stops.`}</div>
          )}
          {!intake.enabled && blockers.length > 0 && (
            <div style={{ ...hint, color: 'var(--status-warning-text, #92400e)' }}>{`Before it can be turned on, save: ${blockers.join(', ')}.`}</div>
          )}
          {!intake.enabled && !blockers.length && dirty && <div style={hint}>Save your changes before turning it on.</div>}

          <h4 style={{ margin: '16px 0 8px', fontSize: 13 }}>Consent (what the platform&apos;s box says)</h4>
          <div style={row}>
            <div>
              <label style={label} htmlFor="wh-consent-source">Platform name</label>
              <input id="wh-consent-source" style={{ ...input, width: 260 }} value={consent.consent_source} onChange={setC('consent_source')}
                placeholder="Race Roster registration" />
            </div>
            <div>
              <label style={label} htmlFor="wh-consent-version">Wording version</label>
              <input id="wh-consent-version" style={{ ...input, width: 140 }} value={consent.consent_wording_version}
                onChange={setC('consent_wording_version')} placeholder="2026-10 v1" />
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <label style={label} htmlFor="wh-consent-wording">The exact wording of the &quot;Yes&quot; box (up to {INTAKE_CONSENT_LIMITS.consent_wording.toLocaleString('en-CA')} characters)</label>
            <textarea id="wh-consent-wording" rows={2} style={{ ...input, width: '100%' }} value={consent.consent_wording}
              onChange={setC('consent_wording')} placeholder="Yes, Coach Cedar may email me about training for this event." />
          </div>
          <div style={{ ...row, marginTop: 8 }}>
            <div>
              <label style={label} htmlFor="wh-optin-path">Opt-in check: path (optional)</label>
              <input id="wh-optin-path" style={{ ...input, width: 220 }} value={consent.optin_path} onChange={setC('optin_path')}
                placeholder="answers.0.value" />
            </div>
            <div>
              <label style={label} htmlFor="wh-optin-value">must equal</label>
              <input id="wh-optin-value" style={{ ...input, width: 120 }} value={consent.optin_value} onChange={setC('optin_value')}
                placeholder="Yes" />
            </div>
            <div style={hint}>When set, anyone whose answer isn&apos;t this is not stored at all.</div>
          </div>

          <h4 style={{ margin: '16px 0 8px', fontSize: 13 }}>Field map</h4>
          <div style={hint}>
            Which field of the platform&apos;s record fills each of ours. Anything not mapped is kept as an extra field (payment
            details, secrets, government IDs, emergency contacts and medical details are never stored).
            {seen.length ? ' Pick from the keys of the last delivery, or type a path.' : ' After the first delivery, its keys appear here to pick from.'}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
            <thead>
              <tr><th style={th}>Our field</th><th style={th}>Comes from</th><th style={th}>Path or value</th>{seen.length > 0 && <th style={th}>Pick a key</th>}</tr>
            </thead>
            <tbody>
              {INTAKE_FIELDS.map((f) => {
                const e = map[f];
                const canConst = (CONSTANT_FIELDS as readonly string[]).includes(f);
                return (
                  <tr key={f}>
                    <td style={td}>{INTAKE_FIELD_LABELS[f]}{f === 'email' || f === 'first_name' ? ' *' : ''}</td>
                    <td style={td}>
                      <select id={`wh-mode-${f}`} style={input} value={e.mode}
                        onChange={(ev) => setField(f, { mode: ev.target.value as FieldMapEntry['mode'] })}>
                        <option value="none">Not mapped</option>
                        <option value="path">Their field</option>
                        {canConst && <option value="value">One value for everyone</option>}
                      </select>
                    </td>
                    <td style={td}>
                      {e.mode === 'path' && (
                        <input id={`wh-path-${f}`} style={{ ...input, width: 240 }} value={e.path} placeholder="participant.email"
                          onChange={(ev) => setField(f, { path: ev.target.value })} />
                      )}
                      {e.mode === 'value' && (
                        <input id={`wh-value-${f}`} style={{ ...input, width: 240 }} value={e.value} placeholder="Ottawa Race Weekend"
                          onChange={(ev) => setField(f, { value: ev.target.value })} />
                      )}
                    </td>
                    {seen.length > 0 && (
                      <td style={td}>
                        <select id={`wh-pick-${f}`} style={input} value=""
                          onChange={(ev) => { if (ev.target.value) setField(f, { mode: 'path', path: concretePath(ev.target.value) }); }}>
                          <option value="">—</option>
                          {seen.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ ...row, marginTop: 8 }}>
            <AdminButton size="sm" onClick={save} disabled={busy || !dirty}>Save webhook settings</AdminButton>
            {diff.errors.length > 0 && <span style={{ ...hint, color: 'var(--status-error-text, #991b1b)' }}>{diff.errors[0]}</span>}
          </div>

          {latest && latest.keys?.length > 0 && (
            <>
              <h4 style={{ margin: '16px 0 8px', fontSize: 13 }}>Keys in the last delivery (keys only, never values)</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Key</th><th style={th}>Used as</th></tr></thead>
                <tbody>
                  {latest.keys.map((k) => (
                    <tr key={k.path}><td style={td}><code>{k.path}</code></td><td style={td}>{keyUseLabel(k.use)}</td></tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <h4 style={{ margin: '16px 0 8px', fontSize: 13 }}>Recent deliveries</h4>
          {deliveries.length === 0 && <div style={hint}>None yet.</div>}
          {deliveries.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Received</th><th style={th}>Outcome</th><th style={th}>People</th><th style={th}>Problems</th></tr></thead>
              <tbody>
                {deliveries.map((d, i) => (
                  <tr key={`${d.received_at}-${i}`}>
                    <td style={td}>{fmtDateTime(d.received_at)}<div style={hint}>{`HTTP ${d.status_code}`}</div></td>
                    <td style={td}>{outcomeLabel(d.intake_outcome)}</td>
                    <td style={td}>{deliveryCountsText(d)}</td>
                    <td style={td}>
                      {(d.problems || []).length === 0 ? '—' : problemLines(d).map((line, j) => <div key={`${j}-${line}`}>{line}</div>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {confirmRotate && (
        <TypedConfirm
          title={`Rotate the webhook URL for ${tag.tag}?`}
          body={<>A new URL is issued and shown once. The old URL keeps working for a while (the server says until when), then stops — update the platform&apos;s webhook before then.</>}
          expected="ROTATE"
          confirmLabel="Rotate the URL"
          danger={false}
          busy={busy}
          onConfirm={() => issue(true)}
          onCancel={() => setConfirmRotate(false)}
        />
      )}
    </AdminCard>
  );
};

export default PartnerWebhookCard;
